/* Replacement filter landing (sections/pk-filter-offer.liquid), Bank only.
 *
 * Prices the Bank filter the way checkout does: the standard price for guests,
 * 50% off (price - floor(price / 2) per filter) for a signed-in active Pup Club
 * member or when Pup Club is in the cart (pk-club-cart-state.js). Adds the
 * chosen quantity of the one-time filter to the cart, then opens the cart drawer
 * exactly like the purifier and the mat do (2026-10-08; it used to go to /cart).
 * The button says what it does: ADD TO CART. Also runs the gallery and accordions that the
 * purifier offer script would run, since that script is not loaded here.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-pk-filter-landing]');
  if (!root) return;

  const money = (cents) => `$${(cents / 100).toFixed(2)}`;
  const half = (cents) => cents - Math.floor(cents / 2);
  const unit = Number(root.dataset.pkUnitCents) || 0;
  const variantId = Number(root.dataset.pkVariantId) || 0;
  const form = root.querySelector('[data-pk-filter-form]');
  const submit = root.querySelector('[data-pk-filter-submit]');
  const status = root.querySelector('[data-pm-form-status]');

  /* Same gate as pk-vip-catalog-state.js: a verified active entitlement plus the
     Shopify sign-in, or the server-verified merchant review session, which the
     cart already prices as a member. Stored or unverified identity never counts. */
  const verifiedDemo = () => {
    try { return window.PKPupClub?.signedIn() === true && window.PKPupClub?.isReviewAccount() === true; } catch (_) { return false; }
  };
  /* One member-price rule (2026-10-09), word for word the same in
     pk-vip-catalog-state.js and pk-filter-offer.js: a verified active member with the
     Shopify sign-in or the verified test account, or Pup Club in the cart for a
     signed-out shopper only (a signed-in account cannot join, so its cart line is
     never priced as membership). Display only; checkout prices are Shopify's. */
  const signedOut = () => {
    const lock = window.PKDemoActionLock;
    if (lock && typeof lock.identity === 'function') { try { return lock.identity() === 'guest'; } catch (_) { return false; } }
    return window.PK_PUP_CLUB_AUTH?.authenticated !== true;
  };
  const memberPriceShown = (activeMember) => (activeMember && (window.PK_CUSTOMER?.loggedIn === true || verifiedDemo()))
    || (window.PK_CLUB_IN_CART === true && signedOut() && !verifiedDemo());
  const clubPrice = () => {
    const auth = window.PK_PUP_CLUB_AUTH || {};
    const activeMember = auth.authenticated === true && auth.activeMember === true;
    return memberPriceShown(activeMember);
  };

  const selected = () => root.querySelector('[data-pk-filter-qty]:checked') || root.querySelector('[data-pk-filter-qty]');
  const quantity = () => Number(selected()?.value) || 1;

  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };

  function paint() {
    const qty = quantity();
    const standard = unit * qty;
    const club = clubPrice();
    const total = club ? half(unit) * qty : standard;
    root.classList.toggle('pk-filter-offer--club', club);
    root.querySelectorAll('[data-pm-tier-card]').forEach((card) => {
      card.classList.toggle('is-selected', card.querySelector('[data-pk-filter-qty]') === selected());
    });
    setText(root.querySelector('[data-pm-price]'), money(total));
    setText(root.querySelector('[data-pm-compare-price]'), club ? money(standard) : '');
    setText(root.querySelector('[data-pm-save]'), club ? `You save ${money(standard - total)}` : '');
    if (submit && !submit.disabled) setText(root.querySelector('[data-pm-cta-label]'), `${money(total)} - ADD TO CART →`);
  }

  root.addEventListener('change', (event) => { if (event.target.matches('[data-pk-filter-qty]')) paint(); });
  ['pk:club-cart', 'pk:member-state', 'pk:auth-state'].forEach((name) => addEventListener(name, paint));
  paint();

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!submit || submit.disabled || !(variantId > 0)) return;
    submit.disabled = true;
    setText(status, 'Adding your filters…');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity() }] })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'We could not add your filters. Your cart was not changed.');
      setText(status, '');
      document.dispatchEvent(new CustomEvent('pm:cart-updated', { bubbles: true, detail: { action: 'add-filter', items: [{ id: variantId, quantity: quantity() }] } }));
      const drawer = document.querySelector('pm-cart-drawer');
      if (typeof drawer?.openFromCartUpdate === 'function') {
        await drawer.openFromCartUpdate(submit);
        submit.disabled = false;
        paint();
        return;
      }
      location.assign('/cart');
    } catch (error) {
      submit.disabled = false;
      setText(status, error instanceof TypeError ? 'We couldn’t reach your cart. Please try again. Nothing was added.' : error.message);
    }
  });

  /* Gallery: thumbnails, arrows, dots and swipe, the same states the purifier gallery uses. */
  const slides = [...root.querySelectorAll('[data-pm-gallery-image]')];
  const thumbs = [...root.querySelectorAll('[data-pm-gallery-thumbnail]')];
  const dots = [...root.querySelectorAll('[data-pm-gallery-dot]')];
  let current = 0;
  function show(index) {
    current = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const on = i === current;
      slide.classList.toggle('is-active', on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      slide.toggleAttribute('inert', !on);
      const video = slide.querySelector('video');
      if (video) {
        if (on) { video.controls = true; } else { video.pause(); }
      }
    });
    thumbs.forEach((thumb, i) => { thumb.classList.toggle('is-active', i === current); thumb.setAttribute('aria-pressed', i === current ? 'true' : 'false'); });
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
  }
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
  root.querySelector('[data-pm-gallery-previous]')?.addEventListener('click', () => show(current - 1));
  root.querySelector('[data-pm-gallery-next]')?.addEventListener('click', () => show(current + 1));
  const stage = root.querySelector('[data-pm-gallery-stage]');
  stage?.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') show(current - 1);
    if (event.key === 'ArrowRight') show(current + 1);
  });
  let startX = null;
  stage?.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
  stage?.addEventListener('touchend', (event) => {
    if (startX === null) return;
    const dx = event.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 40) show(current + (dx < 0 ? 1 : -1));
    startX = null;
  });

  /* Accordions. */
  root.querySelectorAll('[data-pm-accordion-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      const open = trigger.getAttribute('aria-expanded') !== 'true';
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
    });
  });
})();
